/**
 * Position indicator for the mobile category carousel.
 *
 * The grid is a plain scroll-snap flex row below 750px and a static three
 * column grid above it, where the dots are hidden and `scrollLeft` never
 * moves. So the same listener is safe to keep in both layouts.
 */
class VelaEditorialDots extends HTMLElement {
  #grid = null;
  #dots = [];
  #frame = 0;

  connectedCallback() {
    this.#grid = this.closest('.vela-editorial')?.querySelector('.vela-editorial__grid');
    this.#dots = Array.from(this.querySelectorAll('[data-dot-index]'));
    if (!this.#grid || this.#dots.length === 0) return;

    this.#grid.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#grid?.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('click', this.#onClick);
    cancelAnimationFrame(this.#frame);
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#setActive(this.#currentIndex()));
  };

  #onClick = (event) => {
    const dot = event.target.closest('[data-dot-index]');
    if (!dot) return;

    const index = Number(dot.dataset.dotIndex);
    this.#grid.scrollTo({ left: index * this.#grid.clientWidth, behavior: 'smooth' });
    this.#setActive(index);
  };

  #currentIndex() {
    const width = this.#grid.clientWidth;
    if (width === 0) return 0;
    const index = Math.round(this.#grid.scrollLeft / width);
    return Math.min(Math.max(index, 0), this.#dots.length - 1);
  }

  #setActive(index) {
    this.#dots.forEach((dot, position) => {
      if (position === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('vela-editorial-dots')) {
  customElements.define('vela-editorial-dots', VelaEditorialDots);
}
